import {
    Component,
    Input,
    OnInit,
    Output,
    OnDestroy,
    OnChanges,
    SimpleChanges
} from '@angular/core';
import { ReplaySubject, Subject, BehaviorSubject } from 'rxjs';
import { debounceTime, takeUntil, distinctUntilChanged } from 'rxjs/operators';
import { CompanySetting } from '../../company-setting';
import { LoggingService } from '../../shared/services/logging';
import { min } from 'moment';

@Component({
    selector: 'ef-password-change-presenter',
    templateUrl: './password-change-presenter.component.html',
    styleUrls: ['./password-change-presenter.component.scss'],
    standalone: false
})
export class PasswordChangePresenterComponent
    implements OnInit, OnDestroy, OnChanges
{
    oldPasswordValid = false;
    oldPassword = '';
    newPassword = '';
    confirmPassword = '';
    passwordsMatch = false;
    updateBtnEnabled = false;
    @Input() updateResponseMessage = '';
    @Input() validateOldPasswordResponse: boolean;
    @Input() updateResponseStatusCode = '';
    @Input() infoMessage = '';
    @Input() minPasswordLength = 0;
    @Output() ValidateOldPassword = new ReplaySubject<string>(1);
    @Output() UpdatePassword = new ReplaySubject<string>(1);
    @Output() Logout = new ReplaySubject<any>(1);
    private destroy$ = new Subject<void>();
    private oldPasswordSubject = new BehaviorSubject<string>('');
    private newPasswordSubject = new BehaviorSubject<string>('');
    private confirmPasswordSubject = new BehaviorSubject<string>('');
    newPasswordErrorIcon: boolean;
    newPasswordIconTooltip = '';
    confirmPasswordErrorIcon: boolean;
    confirmPasswordIconTooltip = '';
    oldPasswordIconTooltip = '';

    /**
     *
     */
    constructor(private log: LoggingService) {}
    ngOnInit(): void {
        // Set up debounced old password validation
        this.oldPasswordSubject
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                takeUntil(this.destroy$)
            )
            .subscribe(password => {
                if (password) {
                    this.ValidateOldPassword.next(password);
                    this.oldPasswordValid = true;
                    this.updateButtonState();
                }
            });

        // Set up debounced new password validation
        this.newPasswordSubject
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.onNewPasswordChange();
            });

        // Set up debounced confirm password validation
        this.confirmPasswordSubject
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.onConfirmPasswordChange();
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.log.verbose(
            'PasswordChangePresenterComponent',
            'ngOnChanges',
            changes
        );
        // Update button state only when validateOldPasswordResponse changes
        if (
            changes['validateOldPasswordResponse'] ||
            changes['updateResponseStatusCode']
        ) {
            this.updateButtonState();
            if (changes['updateResponseStatusCode']) {
                if (changes['updateResponseStatusCode'].currentValue === '3') {
                    this.oldPassword = '';
                    this.newPassword = '';
                    this.confirmPassword = '';
                    this.oldPasswordSubject.next(this.oldPassword);
                    this.newPasswordSubject.next(this.newPassword);
                    this.confirmPasswordSubject.next(this.confirmPassword);
                    this.newPasswordErrorIcon = null;
                    this.confirmPasswordErrorIcon = null;
                    this.updateBtnEnabled = false;
                }
            }

        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    validateOldPassword() {
        // Trigger debounced validation
        this.oldPasswordSubject.next(this.oldPassword);
    }

    onNewPasswordChange() {
        this.updateButtonState();
    }

    onConfirmPasswordChange() {
        this.updateButtonState();
    }

    private updateButtonState() {
        const passwordsMatch = this.newPassword === this.confirmPassword;
        const newPasswordDifferent = this.newPassword !== this.oldPassword;
        const oldPasswordValid = this.validateOldPasswordResponse === true;
        const newPasswordNotEmpty = !!(
            this.newPassword && this.newPassword.trim() !== ''
        );
        const confirmPasswordNotEmpty = !!(
            this.confirmPassword && this.confirmPassword.trim() !== ''
        );
        const passwordNotAlreadyChanged = this.updateResponseStatusCode !== '1';
        const newPasswordContainsUppercase = /[A-Z]/.test(this.newPassword);
        const newPasswordContainsLowercase = /[a-z]/.test(this.newPassword);
        const newPasswordLengthValid = !!(this.newPassword && this.newPassword.length >= this.minPasswordLength);

        this.passwordsMatch = passwordsMatch;
        this.updateBtnEnabled =
            passwordsMatch &&
            newPasswordDifferent &&
            newPasswordContainsUppercase &&
            newPasswordContainsLowercase &&
            newPasswordLengthValid &&
            oldPasswordValid &&
            newPasswordNotEmpty &&
            confirmPasswordNotEmpty &&
            passwordNotAlreadyChanged;

        //log all the variables
        this.log.verbose({
            type: 'PasswordChangePresenterComponent:updateButtonState',
            passwordsMatch,
            newPasswordDifferent,
            oldPasswordValid,
            newPasswordNotEmpty,
            confirmPasswordNotEmpty,
            passwordNotAlreadyChanged,
            updateResponseStatusCode: this.updateResponseStatusCode,
            updateBtnEnabled: this.updateBtnEnabled
        });

        this.newPasswordIconTooltip = '';
        this.confirmPasswordIconTooltip = '';
        this.oldPasswordIconTooltip = '';

        //Old Password Checks
        if (!oldPasswordValid) {
            this.oldPasswordIconTooltip = 'Old password is not valid';
            this.log.error('Old password is not valid');
        } else {
            this.oldPasswordIconTooltip = 'Old password is valid';
        }

        // New Password Checks
        if (!newPasswordNotEmpty) {
            this.newPasswordErrorIcon = true;
            this.newPasswordIconTooltip = 'New password cannot be empty';
            this.log.error('New password cannot be empty');
        } else if (!newPasswordDifferent) {
            this.newPasswordErrorIcon = true;
            this.newPasswordIconTooltip = 'New password must be different from old password';
            this.log.error('New password must be different from old password');
        } else if (!newPasswordContainsUppercase) {
            this.newPasswordErrorIcon = true;
            this.newPasswordIconTooltip = 'New password must contain at least one uppercase letter';
            this.log.error('New password must contain at least one uppercase letter');
        } else if (!newPasswordContainsLowercase) {
            this.newPasswordErrorIcon = true;
            this.newPasswordIconTooltip = 'New password must contain at least one lowercase letter';
            this.log.error('New password must contain at least one lowercase letter');
        } else if (!newPasswordLengthValid) {
            this.newPasswordErrorIcon = true;
            this.newPasswordIconTooltip = `New password must be at least ${this.minPasswordLength} characters long`;
            this.log.error(`New password must be at least ${this.minPasswordLength} characters long`);
        } else {
            this.newPasswordErrorIcon = false;
            this.newPasswordIconTooltip = 'New password is valid';
            this.log.verbose('New password is valid');
        }

        // Confirm Password Checks
        if (!confirmPasswordNotEmpty) {
            this.confirmPasswordErrorIcon = true;
            this.confirmPasswordIconTooltip = 'Confirm password cannot be empty';
            this.log.error('Confirm password cannot be empty');
        } else if (!passwordsMatch) {
            this.confirmPasswordErrorIcon = true;
            this.confirmPasswordIconTooltip = 'Passwords do not match';
            this.log.error('Passwords do not match');
        } else if (!passwordNotAlreadyChanged) {
            this.log.error('Password has already been successfully changed');
        } else {
            this.confirmPasswordErrorIcon = false;
            this.confirmPasswordIconTooltip = 'Password is valid';
            this.log.verbose('All conditions met - button enabled');
        }
    }

    onNewPasswordInput() {
        // Trigger debounced validation
        this.newPasswordSubject.next(this.newPassword);
    }
    onConfirmPasswordInput() {
        // Trigger debounced validation
        this.confirmPasswordSubject.next(this.confirmPassword);
    }
    changePassword(): void {
        if (this.oldPasswordValid === true && this.passwordsMatch === true) {
            // update the password user setting with the new password
            this.UpdatePassword.next(this.confirmPassword);
            // clear the token and redirect to login page
        }
    }
    gotoLogin() {
        this.Logout.next();
    }
}
